import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';import { BACKEND_URL } from '../config/canonical';
import { linkOneSignalUser, unlinkOneSignalUser } from '../utils/onesignal';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Iter131g — Register the browser FCM push token as soon as we know
  // who the user is. Fires on every fresh auth session (login OR page
  // reload) and is completely idempotent (enablePush no-ops if the
  // browser has already denied permission).
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('nexus_token');
    if (!token) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      import('../services/firebase').then(({ enablePush }) => {
        enablePush({ backendUrl: BACKEND_URL, authToken: token }).catch(() => {});
      }).catch(() => {});
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [user]);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('nexus_token');
    if (!token) {
      setLoading(false);
      return;
    }

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${BACKEND_URL}/api/auth/me`, true);
      xhr.withCredentials = true; // Enable cookies
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const userData = JSON.parse(xhr.responseText);
            setUser(userData);
          } catch {
            localStorage.removeItem('nexus_token');
            setUser(null);
          }
        } else {
          localStorage.removeItem('nexus_token');
          setUser(null);
        }
        setLoading(false);
        resolve();
      };
      
      xhr.onerror = function() {
        console.error('Auth fetch error');
        setUser(null);
        setLoading(false);
        resolve();
      };
      
      xhr.send();
    });
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Master Directive #32 — apply the user's saved accent color as a CSS
  // variable so accent UI elements (highlights, focus rings, chips) pick
  // it up globally. White/black are rejected server-side; if the value is
  // missing we fall back to the Nexus amber accent baked into index.css.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const c = user?.accent_color;
    if (c && typeof c === "string" && c.trim()) {
      root.style.setProperty("--nx-user-accent", c);
    } else {
      root.style.removeProperty("--nx-user-accent");
    }
  }, [user?.accent_color]);

  // Link the signed-in user to OneSignal (external_id + email alias + push sub)
  useEffect(() => {
    if (user?.id) {
      const token = localStorage.getItem('nexus_token');
      if (token) linkOneSignalUser(user, token);
    }
  }, [user?.id]);

  const login = async (username, password) => {
    setError(null);
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BACKEND_URL}/api/auth/login`, true);
      xhr.withCredentials = true; // Enable cookies
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        let data = {};
        try {
          data = JSON.parse(xhr.responseText);
        } catch (e) {
          const error = new Error('Invalid server response');
          setError(error.message);
          reject(error);
          return;
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          localStorage.setItem('nexus_token', data.token);
          setUser(data.user);
          resolve(data);
        } else {
          const error = new Error(data.detail || 'Login failed');
          setError(error.message);
          reject(error);
        }
      };
      
      xhr.onerror = function() {
        const error = new Error('Network error');
        setError(error.message);
        reject(error);
      };
      
      xhr.send(JSON.stringify({ username, password }));
    });
  };

  const logout = async () => {
    // Call logout endpoint to clear httpOnly cookies
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BACKEND_URL}/api/auth/logout`, true);
      xhr.withCredentials = true;
      xhr.send();
    } catch (e) {
      console.error('Logout request failed:', e);
    }
    localStorage.removeItem('nexus_token');
    setUser(null);
    unlinkOneSignalUser();
  };

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    logout,
    refreshUser: fetchUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'department_admin' || user?.role === 'main_hub',
    isInvestigator: ['field_investigator', 'skip_tracer', 'transporter'].includes(user?.role),
    isClient: user?.role === 'client',
    token: localStorage.getItem('nexus_token')
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
